import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { Level, PhysicalUnit, GraphicPlan, GraphicPlanRegion } from '../../types';
import { PdfKonvaViewer, PdfRegion } from './PdfKonvaViewer';
import { List } from 'react-window';

interface GraphicProgressConfigProps {
    activeProjectId: string;
    levels: Level[];
    units: PhysicalUnit[];
    canEdit?: boolean;
    onClose?: () => void;
}

export const GraphicProgressConfig: React.FC<GraphicProgressConfigProps> = ({
    activeProjectId,
    levels,
    units,
    canEdit = true,
    onClose
}) => {
    const [plans, setPlans] = useState<GraphicPlan[]>([]);
    const [regions, setRegions] = useState<GraphicPlanRegion[]>([]);
    const [loading, setLoading] = useState(true);
    
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [selectedLevelId, setSelectedLevelId] = useState<string>('all');
    const [searchUnit, setSearchUnit] = useState('');
    const listContainerRef = useRef<HTMLDivElement>(null);
    const [listSize, setListSize] = useState({ width: 0, height: 400 });
    const [hoverTooltip, setHoverTooltip] = useState<{ visible: boolean, x: number, y: number, text: string } | null>(null);
        // Upload state
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [planToReplace, setPlanToReplace] = useState<GraphicPlan | null>(null);

    // Seleccionados para interactuar
    const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);

    // Fallbacks defensivos para props (prevención de nulos absolutos)
    const safeUnits = units || [];
    const safeLevels = levels || [];

    // Helper functions for revision control
    const getPlanRevision = (plan: GraphicPlan): number => {
        if (!plan.nombre_original) return 1;
        const match = plan.nombre_original.match(/\|rev:(\d+)$/);
        return match ? parseInt(match[1]) : 1;
    };

    const getCleanOriginalName = (plan: GraphicPlan): string => {
        if (!plan.nombre_original) return '';
        return plan.nombre_original.split('|rev:')[0];
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [plansRes, regionsRes] = await Promise.all([
                supabase.from('graphic_plans').select('*').eq('project_id', activeProjectId),
                supabase.from('graphic_plan_regions').select('*').eq('project_id', activeProjectId)
            ]);
            
            if (plansRes.data) {
                setPlans(plansRes.data);
                if (plansRes.data.length > 0 && !selectedPlanId) {
                    setSelectedPlanId(plansRes.data[0].id);
                }
            } else {
                setPlans([]);
            }

            if (regionsRes.data) {
                setRegions(regionsRes.data);
            } else {
                setRegions([]);
            }
        } catch (err) {
            console.error("Error cargando configuración", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeProjectId) loadData();
    }, [activeProjectId]);

    useEffect(() => {
        const updateListSize = () => {
            if (listContainerRef.current) {
                setListSize({
                    width: listContainerRef.current.offsetWidth,
                    height: listContainerRef.current.offsetHeight
                });
            }
        };
        const obs = new ResizeObserver(() => requestAnimationFrame(updateListSize));
        if (listContainerRef.current) obs.observe(listContainerRef.current);
        return () => obs.disconnect();
    }, []);

    const activePlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);
    const activePlanRegions = useMemo(() => regions.filter(r => r.plan_id === selectedPlanId), [regions, selectedPlanId]);

    const handleUploadPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !activeProjectId) return;

        if (planToReplace) {
            const confirmReplace = window.confirm(
                `¿Está seguro de que desea subir una nueva revisión para el plano "${planToReplace.nombre_visible}"?\n\nSe reemplazará el archivo PDF, pero todas las unidades físicas asociadas quedarán en la misma posición.`
            );
            if (!confirmReplace) {
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }
        }

        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
            const filePath = `${activeProjectId}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('graphic_plans')
                .upload(filePath, file);

            if (uploadError) {
                alert(`Error al subir archivo: ${uploadError.message}`);
                return;
            }

            if (planToReplace) {
                // Reemplazo: incrementa la versión y actualiza
                const currentRev = getPlanRevision(planToReplace);
                const nextRev = currentRev + 1;
                const newNombreOriginal = `${file.name}|rev:${nextRev}`;

                const { error: updateError } = await supabase
                    .from('graphic_plans')
                    .update({
                        nombre_original: newNombreOriginal,
                        ruta_storage: filePath
                    })
                    .eq('id', planToReplace.id);

                if (updateError) {
                    alert(`Error al actualizar revisión del plano: ${updateError.message}`);
                    return;
                }

                try {
                    await supabase.storage.from('graphic_plans').remove([planToReplace.ruta_storage]);
                } catch (e) {
                    console.warn("Error borrando anterior archivo", e);
                }

                setPlans(prev => prev.map(p => p.id === planToReplace.id ? {
                    ...p,
                    nombre_original: newNombreOriginal,
                    ruta_storage: filePath
                } : p));

                alert(`Nueva revisión (Rev. ${nextRev}) del plano subida con éxito.`);
            } else {
                // Nuevo plano: inserta
                const { data, error: insertError } = await supabase.from('graphic_plans').insert({
                    project_id: activeProjectId,
                    nombre_visible: file.name.replace('.pdf', ''),
                    nombre_original: `${file.name}|rev:1`,
                    ruta_storage: filePath
                }).select();

                if (insertError) {
                    alert(`Error al guardar plano: ${insertError.message}`);
                    return;
                }

                if (data && data.length > 0) {
                    setPlans(prev => [...prev, data[0]]);
                    setSelectedPlanId(data[0].id);
                    alert("Plano nuevo subido con éxito (Rev. 1).");
                }
            }
            
        } catch (err) {
            console.error("Excepción en subida", err);
        } finally {
            setIsUploading(false);
            setPlanToReplace(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeletePlan = async () => {
        if (!activePlan) return;
        const confirmDelete = window.confirm(
            `¿Está seguro de que desea eliminar el plano "${activePlan.nombre_visible}" y todas sus regiones asociadas?\n\nEsta acción no se puede deshacer.`
        );
        if (!confirmDelete) return;
        
        try {
            await supabase.from('graphic_plans').delete().eq('id', activePlan.id);
            await supabase.storage.from('graphic_plans').remove([activePlan.ruta_storage]);
            
            setPlans(prev => prev.filter(p => p.id !== activePlan.id));
            setRegions(prev => prev.filter(r => r.plan_id !== activePlan.id));
            setSelectedPlanId('');
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddUnitToPlan = async (unit: PhysicalUnit) => {
        if (!activePlan) return alert("Selecciona un plano primero");
        
        if (activePlanRegions.some(r => r.physical_unit_id === unit.id)) {
            return alert("Esta unidad ya está vinculada a este plano.");
        }

        const newRegion = {
            project_id: activeProjectId,
            plan_id: activePlan.id,
            level_id: unit.levelId,
            physical_unit_id: unit.id,
            page_number: 1,
            x_percent: 40,
            y_percent: 40,
            width_percent: 10,
            height_percent: 10,
            label: unit.name,
            is_locked: true
        };

        try {
            const { data, error } = await supabase.from('graphic_plan_regions').insert(newRegion).select();
            if (error) throw error;
            if (data && data.length > 0) {
                setRegions(prev => [...prev, data[0]]);
                setSelectedRegionId(data[0].id);
            }
        } catch (err) {
            console.error("Error añadiendo región", err);
        }
    };

    const handleRemoveRegion = async (regionId: string) => {
        if (!window.confirm("¿Quitar unidad del plano?")) return;
        try {
            await supabase.from('graphic_plan_regions').delete().eq('id', regionId);
            setRegions(prev => prev.filter(r => r.id !== regionId));
            if (selectedRegionId === regionId) setSelectedRegionId(null);
        } catch (err) {
            console.error("Error eliminando región", err);
        }
    };

    const handleRegionUpdate = async (updatedRegion: PdfRegion) => {
        setRegions(prev => prev.map(r => r.id === updatedRegion.id ? {
            ...r,
            x_percent: updatedRegion.xPercent,
            y_percent: updatedRegion.yPercent,
            width_percent: updatedRegion.widthPercent,
            height_percent: updatedRegion.heightPercent,
            is_locked: updatedRegion.isLocked
        } : r));

        try {
            await supabase.from('graphic_plan_regions')
                .update({
                    x_percent: updatedRegion.xPercent,
                    y_percent: updatedRegion.yPercent,
                    width_percent: updatedRegion.widthPercent,
                    height_percent: updatedRegion.heightPercent,
                    is_locked: updatedRegion.isLocked
                })
                .eq('id', updatedRegion.id);
        } catch (err) {
            console.error("Error syncing region update", err);
        }
    };

    const konvaRegions: PdfRegion[] = useMemo(() => {
        return activePlanRegions.map(reg => {
            const unit = safeUnits.find(u => u?.id === reg.physical_unit_id);
            const level = safeLevels.find(l => l?.id === reg.level_id);
            const shortLabel = unit ? (level ? `${level.name?.substring(0,2)}-${unit.name}` : unit.name) : '';
            return {
                id: reg.id,
                pageNumber: reg.page_number || 1,
                xPercent: reg.x_percent || 0,
                yPercent: reg.y_percent || 0,
                widthPercent: reg.width_percent || 10,
                heightPercent: reg.height_percent || 10,
                label: reg.label || shortLabel,
                color: selectedRegionId === reg.id ? 'rgba(59, 130, 246, 0.4)' : 'rgba(251, 191, 36, 0.4)',
                isLocked: reg.is_locked ?? true
            };
        });
    }, [activePlanRegions, safeUnits, safeLevels, selectedRegionId]);

    const filteredUnits = useMemo(() => {
        return safeUnits.filter(u => {
            if (!u) return false;
            if (selectedLevelId !== 'all' && u.levelId !== selectedLevelId) return false;
            if (searchUnit && u.name && !u.name.toLowerCase().includes(searchUnit.toLowerCase())) return false;
            return true;
        });
    }, [safeUnits, selectedLevelId, searchUnit]);

    const itemData = useMemo(() => ({
        filteredUnits,
        activePlanRegions,
        handleAddUnitToPlan,
        handleRemoveRegion,
        canEdit
    }), [filteredUnits, activePlanRegions, canEdit]);

    return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900 absolute inset-0 z-[60]">
            <header className="h-16 px-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={onClose} className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <span className="material-symbols-outlined text-purple-600">architecture</span>
                            Configuración Avance Gráfico
                        </h1>
                    </div>
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Izquierdo: Gestión de Planos y Unidades */}
                <div className="w-80 border-r border-slate-200 dark:border-slate-800 flex flex-col bg-slate-50 dark:bg-slate-900 shrink-0">
                    
                    {/* Sección Planos */}
                    <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-col gap-3 bg-white dark:bg-slate-900/50">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">Planos</h3>
                            {canEdit && (
                                <button 
                                    onClick={() => {
                                        setPlanToReplace(null);
                                        fileInputRef.current?.click();
                                    }}
                                    disabled={isUploading}
                                    className="text-blue-600 hover:text-blue-700 font-bold text-xs flex items-center gap-1 disabled:opacity-50"
                                >
                                    <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                    Nuevo Plano
                                </button>
                            )}
                            <input type="file" ref={fileInputRef} onChange={handleUploadPdf} accept="application/pdf" className="hidden" />
                        </div>
                        
                        {plans.length > 0 ? (
                            <div className="flex flex-col gap-2">
                                <select 
                                    value={selectedPlanId} 
                                    onChange={e => setSelectedPlanId(e.target.value)}
                                    title={activePlan?.nombre_visible}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-2 text-sm text-slate-800 dark:text-white font-medium outline-none focus:border-blue-500"
                                >
                                    {plans.map(p => (
                                        <option key={p.id} value={p.id} title={p.nombre_visible}>
                                            {p.nombre_visible}
                                        </option>
                                    ))}
                                </select>
                                
                                {activePlan && (
                                    <div className="flex flex-col gap-2 mt-1">
                                        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-1 font-semibold">
                                            <span>Versión: <strong className="text-blue-600 dark:text-blue-400">Rev. {getPlanRevision(activePlan)}</strong></span>
                                            <span className="truncate max-w-[140px] text-[10px]" title={getCleanOriginalName(activePlan)}>{getCleanOriginalName(activePlan)}</span>
                                        </div>
                                        {canEdit && (
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => {
                                                        setPlanToReplace(activePlan);
                                                        fileInputRef.current?.click();
                                                    }}
                                                    className="flex-1 py-1.5 px-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/30 dark:hover:bg-blue-950/50 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 transition-all"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">publish</span>
                                                    Subir Rev.
                                                </button>
                                                <button 
                                                    onClick={handleDeletePlan}
                                                    className="py-1.5 px-2.5 bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-950/50 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 transition-all"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">delete</span>
                                                    Eliminar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-xs text-slate-500 italic p-2 text-center bg-slate-100 dark:bg-slate-800 rounded">
                                No hay planos cargados
                            </div>
                        )}
                    </div>

                    {/* Sección Unidades Físicas */}
                    <div className="p-4 flex flex-col gap-3 flex-1 overflow-hidden">
                        <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">Unidades Físicas</h3>
                        
                        <select 
                            value={selectedLevelId} 
                            onChange={e => setSelectedLevelId(e.target.value)}
                            className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-1.5 text-sm"
                        >
                            <option value="all">Todos los niveles</option>
                            {safeLevels.map(l => l ? <option key={l.id} value={l.id}>{l.name}</option> : null)}
                        </select>
                        
                        <input 
                            type="text" 
                            placeholder="Buscar unidad..."
                            value={searchUnit}
                            onChange={e => setSearchUnit(e.target.value)}
                            className="w-full p-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded text-sm"
                        />

                        <div className="flex-1 overflow-hidden" ref={listContainerRef}>
                            {listSize.height > 0 && (
                                <List
                                    style={{ height: listSize.height, width: listSize.width || "100%" }}
                                    rowCount={filteredUnits.length}
                                    rowHeight={44}
                                    className="custom-scrollbar"
                                    rowProps={itemData}
                                    rowComponent={(props) => <VirtualUnitRowWrapper {...props} />}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Panel Central: Visor PDF Editable */}
                <div className="flex-1 bg-slate-200 dark:bg-slate-950 relative">
                    {loading && <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 z-20 flex items-center justify-center"><span className="material-symbols-outlined animate-spin text-4xl text-blue-500">sync</span></div>}
                    
                    {activePlan ? (
                        <>
                            <PdfKonvaViewer 
                                pdfUrl={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/graphic_plans/${activePlan.ruta_storage}`}
                                regions={konvaRegions}
                                interactive={canEdit !== false}
                                selectedRegionId={selectedRegionId}
                                onRegionClick={(r) => {
                                    if (!r && selectedRegionId) {
                                        // Auto-lock when clicking away
                                        const currentRegion = konvaRegions.find(reg => reg.id === selectedRegionId);
                                        if (currentRegion && !currentRegion.isLocked) {
                                            handleRegionUpdate({ ...currentRegion, isLocked: true });
                                        }
                                    }
                                    setSelectedRegionId(r ? r.id : null);
                                }}
                                onRegionUpdate={handleRegionUpdate}
                                onRegionHover={(r, e) => {
                                    if (r && e && e.evt) {
                                        setHoverTooltip({
                                            visible: true,
                                            x: e.evt.clientX,
                                            y: e.evt.clientY,
                                            text: r.label || ''
                                        });
                                    } else {
                                        setHoverTooltip(null);
                                    }
                                }}
                            />

                            {/* Tooltip Hover */}
                            {hoverTooltip && hoverTooltip.visible && (
                                <div 
                                    className="fixed z-[100] pointer-events-none bg-slate-900 text-white text-xs font-bold px-3 py-1.5 rounded shadow-lg transform -translate-x-1/2 -translate-y-full mt-[-10px] whitespace-nowrap"
                                    style={{ left: hoverTooltip.x, top: hoverTooltip.y }}
                                >
                                    {hoverTooltip.text}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                                </div>
                            )}

                            {/* Floating Toolbar for Selected Region */}
                            {selectedRegionId && canEdit && (
                                <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-800 shadow-xl border border-blue-500 rounded-lg px-4 py-2 flex items-center gap-4 z-50 animate-fade-in-down">
                                    <span className="font-bold text-sm text-slate-800 dark:text-white">
                                        {konvaRegions.find(r => r.id === selectedRegionId)?.label || 'Unidad seleccionada'}
                                    </span>
                                    
                                    {konvaRegions.find(r => r.id === selectedRegionId)?.isLocked ? (
                                        <button 
                                            onClick={() => {
                                                const currentRegion = konvaRegions.find(reg => reg.id === selectedRegionId);
                                                if (currentRegion) handleRegionUpdate({ ...currentRegion, isLocked: false });
                                            }}
                                            className="bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 transition-colors shadow-sm"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">lock_open</span>
                                            Desbloquear para editar
                                        </button>
                                    ) : (
                                        <>
                                            <button 
                                                onClick={() => {
                                                    const currentRegion = konvaRegions.find(reg => reg.id === selectedRegionId);
                                                    if (currentRegion) handleRegionUpdate({ ...currentRegion, isLocked: true });
                                                }}
                                                className="bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 transition-colors shadow-sm"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">lock</span>
                                                Bloquear (Listo)
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    handleRemoveRegion(selectedRegionId);
                                                    setSelectedRegionId(null);
                                                }}
                                                className="bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 transition-colors shadow-sm"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">link_off</span>
                                                Desvincular
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                            
                            {/* Instrucciones flotantes */}
                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900/80 backdrop-blur text-white px-4 py-2 rounded-full text-xs font-medium shadow-lg pointer-events-none flex items-center gap-2">
                                <span className="material-symbols-outlined text-[16px]">info</span>
                                {canEdit ? "Haz clic en el panel izquierdo en (+) para añadir una unidad. Arrástrala y ajusta su tamaño en el plano." : "Modo visualización. No tienes permisos para modificar el plano."}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <span className="material-symbols-outlined text-6xl mb-2 opacity-50">picture_as_pdf</span>
                            <p>Sube o selecciona un plano para comenzar la configuración</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const VirtualUnitRowWrapper = React.memo(({ index, style, filteredUnits, activePlanRegions, handleAddUnitToPlan, handleRemoveRegion, canEdit }: any) => {
    const u = filteredUnits[index];
    if (!u) return null;
    const region = activePlanRegions.find((r: any) => r.physical_unit_id === u.id);
    
    return (
        <div style={style} className="pr-1 pb-1">
            <div className="flex items-center justify-between p-2 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 h-full">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate pr-2" title={u.name}>{u.name}</span>
                {canEdit && (
                    region ? (
                        <button 
                            onClick={() => handleRemoveRegion(region.id)}
                            className="text-red-500 hover:text-red-700 flex items-center shrink-0"
                            title="Quitar del plano"
                        >
                            <span className="material-symbols-outlined text-[18px]">close</span>
                        </button>
                    ) : (
                        <button 
                            onClick={() => handleAddUnitToPlan(u)}
                            className="text-blue-600 hover:text-blue-800 flex items-center shrink-0"
                            title="Añadir al plano"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_location</span>
                        </button>
                    )
                )}
            </div>
        </div>
    );
});
